import React, {
    FC,
    memo,
    useCallback,
    useMemo,
} from 'react';

import { useStores } from '@/components/hooks';
import type {
    PolicySetting,
    PolicySettingValue,
    PolicySettingsGroup,
} from '@/types/types';
import i18n from '@/content';
import { DAYS_RETENTION_RANGE } from '@/stores/PolicyStore/dataRetention';
import { LIMIT_TO_USERS_VALUE } from '@/consts';

import PolicyTagInputRow from './PolicyTagInputRow';
import PolicyGroupAutocompleteRow from './PolicyTagInputRow/PolicyGroupAutocompleteRow';
import { ListTypeInputs } from './PolicyTagInputRow/interfaces';
import PolicyNumInputRow from './PolicyNumInputRow';
import PolicySwitchRow from './PolicySwitchRow';
import PolicyDropdownRow, { SUPPORTED_IDS_SET, SupportedDropdownIds } from './PolicyDropdownRow';
import PolicyRadioRow from './PolicyRadioRow';
import {
    getSettingsContent,
    shouldShow,
} from './helpers';
import {
    MAP_POLICY_SETTING_ID_TO_TAG_INPUT_TYPE,
    MAP_UI_CHILD_TO_PARENT,
    SETTINGS_NOT_STYLED_AS_CHILD,
} from './constants';
import {
    POLICY_SETTINGS_SUBGROUPS,
    PolicySettingsSubgroup,
} from './settingsGroups';
import renderGroupedSettings from './renderHelpers';
import styles from './PolicySection.module.scss';

interface ValueChanges {
    value: PolicySettingValue;
}

export interface PolicySectionProps {
    settingsGroupType: PolicySettingsGroup;
    policySettings: PolicySetting[];
    filterBySection?: string;
}

const PolicySection: FC<PolicySectionProps> = ({
    settingsGroupType,
    policySettings = [],
    filterBySection,
}) => {
    // Inject dummy "Protect and Track" setting for Data Protection section
    let processedSettings = policySettings;
    if (filterBySection === 'dataProtection' && settingsGroupType === 'access') {
        const dummySetting: PolicySetting = {
            id: 'protectAndTrack',
            public: true,
            description: 'Protect and Track',
            value: true,
            type: 'switch',
        };
        const hasProtectAndTrack = policySettings.some((s) => s.id === 'protectAndTrack');
        if (!hasProtectAndTrack) {
            processedSettings = [dummySetting, ...policySettings];
        }
    }

    const { settingsToDisplay, policySettingsDict } = getSettingsContent(processedSettings);

    const { policyStore } = useStores();
    const editPolicy = useCallback(
        (settingId: string, newValue: PolicySettingValue, settingsToDisable?: string[]): void => {
            const settingChanges: ValueChanges = { value: newValue };
            policyStore.editPolicy(settingId, settingsGroupType, settingChanges, settingsToDisable);
        },
        [settingsGroupType, policyStore],
    );

    const limitToUsersError = policyStore.validateLimitToUsers();
    const { saveAttempted, changedPolicy } = policyStore;
    const isActiveDefaultPolicy = changedPolicy?.active === true && !!changedPolicy?.id;
    const limitToUsersReadOnlyTooltip = i18n.t('policyEditor.limitToUsers.defaultPolicyDisabledTooltip');

    // Group settings by their semantic subgroups
    const groupedSettings = useMemo(() => {
        const subgroups = POLICY_SETTINGS_SUBGROUPS[settingsGroupType] || [];
        const settingsMap = new Map<string, PolicySetting>(
            settingsToDisplay.map((s) => [s.id, s]),
        );

        const assignedSettingIds = new Set<string>();

        let filteredSubgroups = subgroups;
        if (filterBySection) {
            if (filterBySection === 'policyConfiguration' && settingsGroupType === 'access') {
                const configKey = 'policyEditor.access.subgroups.policyConfiguration';
                filteredSubgroups = subgroups.filter((sg) => sg.titleKey === configKey);
            } else if (filterBySection === 'accessControl' && settingsGroupType === 'access') {
                const configKey = 'policyEditor.access.subgroups.policyConfiguration';
                const excludedIds = new Set([
                    'protectAndTrack',
                    'protectOnDownload',
                    'viewerOnly',
                    'passwordProtection',
                    'preventDownloadUnprotected',
                    'applyWatermark',
                    'dataRetention',
                    'dataRetention.Value',
                    'dataRetention.AutoDelete',
                    'language', // Recipient Experience
                ]);
                filteredSubgroups = subgroups.filter(
                    (sg) => sg.titleKey !== configKey && !sg.settingIds.some((id) => excludedIds.has(id)),
                );
            } else if (filterBySection === 'dataProtection' && settingsGroupType === 'access') {
                const dataProtectionIds = new Set([
                    'protectAndTrack',
                    'protectOnDownload',
                    'viewerOnly',
                    'passwordProtection',
                    'preventDownloadUnprotected',
                    'applyWatermark',
                    'dataRetention',
                    'dataRetention.Value',
                    'dataRetention.AutoDelete',
                ]);
                filteredSubgroups = subgroups.filter((sg) => sg.settingIds.some((id) => dataProtectionIds.has(id)));
            } else if (filterBySection === 'otherSettings' && settingsGroupType === 'access') {
                filteredSubgroups = subgroups.filter((sg) => sg.settingIds.includes('language'));
            }
        }

        // Create subgroups with their assigned settings
        const result = filteredSubgroups.map((subgroup: PolicySettingsSubgroup) => {
            const settings = subgroup.settingIds
                .map((id) => {
                    assignedSettingIds.add(id);
                    return settingsMap.get(id);
                })
                .filter((s): s is PolicySetting => s !== undefined && s.public !== false);

            return {
                ...subgroup,
                settings,
            };
        }).filter((group) => group.settings.length > 0);

        // Only show ungrouped settings if we're not filtering by section
        // When filtering, we only want to show the explicitly mapped settings
        if (!filterBySection) {
            const ungroupedSettings = settingsToDisplay.filter(
                (s) => s.public !== false && !assignedSettingIds.has(s.id),
            );

            if (ungroupedSettings.length > 0) {
                result.push({
                    titleKey: 'policyEditor.other',
                    settingIds: ungroupedSettings.map((s) => s.id),
                    settings: ungroupedSettings,
                });
            }
        }

        return result;
    }, [settingsGroupType, settingsToDisplay, filterBySection]);

    const renderSetting = (setting: PolicySetting): React.ReactNode => {
        const {
            id, dependency, type, value, values,
        } = setting;

        const dependencyId: string = dependency || MAP_UI_CHILD_TO_PARENT[id];
        const show: boolean = shouldShow(policySettingsDict, dependencyId, id);
        const hasParentDependency = !!dependencyId;
        const isChild = hasParentDependency && !SETTINGS_NOT_STYLED_AS_CHILD.has(id);

        if (!show) {
            return null;
        }

        switch (type) {
        case 'switch':
            return (
                <PolicySwitchRow
                    key={id}
                    isChild={isChild}
                    settingId={id}
                    settingValue={value as boolean}
                    handleChange={editPolicy}
                    allSettings={policySettings}
                    policyDisabled={id === 'limitToUsers' && isActiveDefaultPolicy}
                    policyDisabledTooltip={
                        id === 'limitToUsers' && isActiveDefaultPolicy
                            ? limitToUsersReadOnlyTooltip
                            : undefined
                    }
                />
            );
        case 'input':
            return (
                <PolicyNumInputRow
                    key={id}
                    isChild={isChild}
                    settingId={id}
                    settingValue={value as number}
                    handleChange={editPolicy}
                    numRange={DAYS_RETENTION_RANGE}
                />
            );
        case 'tag':
        case 'list': {
            const hasError = id === LIMIT_TO_USERS_VALUE && !!limitToUsersError && saveAttempted;
            const errorMessage = hasError ? i18n.t(limitToUsersError.message) : undefined;
            const tagInputType = MAP_POLICY_SETTING_ID_TO_TAG_INPUT_TYPE[id];
            const limitUsersListReadOnly = id === LIMIT_TO_USERS_VALUE && isActiveDefaultPolicy;

            const TagComponent = tagInputType === ListTypeInputs.emailsOrGroups
                ? PolicyGroupAutocompleteRow
                : PolicyTagInputRow;

            return (
                <TagComponent
                    key={id}
                    isChild={isChild}
                    listType={tagInputType}
                    settingId={id}
                    settingValue={value as string[]}
                    handleChange={editPolicy}
                    hasError={hasError}
                    errorMessage={errorMessage}
                    controlReadOnly={limitUsersListReadOnly}
                    readOnlyTooltip={
                        limitUsersListReadOnly ? limitToUsersReadOnlyTooltip : undefined
                    }
                />
            );
        }
        case 'dropdown':
            // Special handling for forwardingOptions and language - render as radio buttons
            if ((id === 'forwardingOptions' || id === 'language') && values) {
                const options = values.map((v) => ({
                    label: i18n.t(`policy.${id}.values.${v}`),
                    value: v as string,
                }));
                return (
                    <PolicyRadioRow
                        key={id}
                        isChild={isChild}
                        settingId={id}
                        settingValue={value as string}
                        handleChange={editPolicy}
                        options={options}
                        allSettings={policySettings}
                    />
                );
            }
            return SUPPORTED_IDS_SET.has(id as SupportedDropdownIds) && (
                <PolicyDropdownRow
                    values={values}
                    key={id}
                    isChild={isChild}
                    settingId={id}
                    settingValue={value as string}
                    handleChange={editPolicy}
                />
            );
        default:
            return null;
        }
    };

    // If filtering resulted in no groups, show all settings ungrouped
    const hasGroupedSettings = groupedSettings.length > 0 && groupedSettings.some((g) => g.settings.length > 0);

    return (
        <div className={styles['section-container']}>
            {hasGroupedSettings ? (
                renderGroupedSettings({
                    groupedSettings,
                    renderSetting,
                    MAP_UI_CHILD_TO_PARENT,
                })
            ) : (
                // Fallback: show all settings without grouping
                <div className={styles.controlsGroup}>
                    {settingsToDisplay.filter((s) => s.public !== false).map((setting) => (
                        <div key={setting.id} className={styles.settingWrapper}>
                            {renderSetting(setting)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(PolicySection);
